import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { BiExport } from 'react-icons/bi';
import { Button, ButtonProps } from '@heroui/button';
import { useTranslation } from 'react-i18next';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, useDisclosure } from '@heroui/modal';

import { exportDbToZip } from '@/core/export.ts';
import SpinnerOverlay from '@/components/Spinner/SpinnerOverlay.tsx';
import { useSongbooks } from '@/hooks/useSongbook.ts';
import Spinner from '@/components/Spinner/Spinner.tsx';
import SongbookTreeItem from '@/pages/Songbook/Songbook/SongbookTreeItem.tsx';

interface SongbookExportButtonProps {
  color?: ButtonProps['color'];
  variant?: ButtonProps['variant'];
  songbookId?: string;
  songId?: string;
}

function ExportButton({ color, variant = 'flat', songbookId, songId }: SongbookExportButtonProps) {
  const { t } = useTranslation();
  const { mutate: exportDb, isPending, reset } = useMutation({
    mutationKey: ['exportDatabase', songbookId, songId],
    mutationFn: () => exportDbToZip({ songId: Object.values(selectedSongs).flat() }),
  });

  const [selectedSongs, setSelectedSongs] = useState<Record<string, string[]>>({});

  const { data: songbooks, isLoading: isSongbooksLoading } = useSongbooks();

  const setSelectedSongsMemo = useMemo(
    (): Record<string, (songs: string[]) => void> => songbooks?.reduce((acc, songbook) => ({
      ...acc,
      [songbook.id]: (songs: string[]) => setSelectedSongs((acc) => ({ ...acc, [songbook.id]: songs })),
    }), {}) || {},
    [songbooks, setSelectedSongs],
);

  const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure({
    onClose() {
      reset();
      setSelectedSongs({});
    }
  });

  const selectedAll = !songbookId && !songId;

  return (
    <>
      <Button
        aria-label="Save songbooks"
        color={color}
        startContent={<BiExport />}
        variant={variant}
        onPress={onOpen}
      >
        {t('songbook.home.export')}
      </Button>
      <SpinnerOverlay isOpen={isPending} />
      <Modal backdrop="blur" isDismissable={!isPending} isOpen={isOpen} onOpenChange={onOpenChange}>
        <ModalContent className="bg-default-50 text-foreground">
          <div className="w-full">
            <ModalHeader className="flex flex-col gap-1">{t('songbook.export.title')}</ModalHeader>
            <ModalBody className="pb-3">
              {isSongbooksLoading && <Spinner />}
              {!isSongbooksLoading && songbooks && (
                <div>
                  <p className="mb-4">
                    {t('songbook.export.description')}
                  </p>
                  <div>
                    {songbooks.map((songbook) => (
                      <SongbookTreeItem
                        key={songbook.id}
                        defaultSong={songId}
                        isSelected={selectedAll || songbookId === songbook.id}
                        selectedSongs={selectedSongs[songbook.id]}
                        setSelectedSongs={setSelectedSongsMemo[songbook.id]}
                        songbook={songbook}
                      />
                    ))}
                  </div>
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="default" disabled={isPending} variant="light" onPress={onClose}>
                {t('songbook.modal.cancel')}
              </Button>
              <Button color="primary" disabled={isPending} startContent={<BiExport />} type="submit" onPress={() => exportDb()}>
                {t('songbook.export.export')}
              </Button>
            </ModalFooter>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
}

export default ExportButton;
